import {useState, useEffect, useMemo} from "react"
import {useAppDispatch, useAppSelector} from "@/store/hooks"
import {selectAuth} from "@/store/auth/authSelectors"
import {debounce} from "@/utils/debounce"
import {DEBOUNCE_INPUT_TIME} from "@/const/config"

import {updateFullNameSaga, updateUsernameSaga, sendEmailChangeSaga, verifyEmailChangeSaga, initiateDeleteAccountSaga} from "@/store/auth/authSlice"
import AccountSettingsTitle from "@/components/modals/AccountSettingsModal/components/AccountSettingsTitle/AccountSettingsTitle"
import FormColumn from "@/components/ui/Form/components/FormColumn/FormColumn"
import FormLabel from "@/components/ui/Form/components/FormLabel/FormLabel"
import FormDivider from "@/components/ui/Form/components/FormDivider/FormDivider"
import FormPanel from "@/components/ui/Form/components/FormPanel/FormPanel"
import FormError from "@/components/ui/Form/components/FormError/FormError"
import FormTitle from "@/components/ui/Form/components/FormTitle/FormTitle"
import Input from "@/components/ui/Input/Input"
import Button from "@/components/ui/Button/Button"
import AccountSettingsAvatar from "@/components/modals/AccountSettingsModal/components/AccountSettingsAvatar/AccountSettingsAvatar"
import IconTrash from "@/assets/icons/icon-trash.svg?react"
import {TextNorm} from "@/components/ui/Text";
import FormRow from "@/components/ui/Form/components/FormRow/FormRow.tsx";

const AccountSettingsModalProfile = () => {
    const dispatch = useAppDispatch()
    const auth = useAppSelector(selectAuth)
    const [fullName, setFullName] = useState(auth?.fullName || '')
    const [username, setUsername] = useState(auth?.username || '')
    const [email, setEmail] = useState(auth?.email || '')
    const [fullNameError, setFullNameError] = useState<string | null>(null)
    const [usernameError, setUsernameError] = useState<string | null>(null)
    const [emailError, setEmailError] = useState<string | null>(null)
    const [emailCodeSent, setEmailCodeSent] = useState(false)
    const [emailCodeError, setEmailCodeError] = useState<string | null>(null)
    const [verificationCode, setVerificationCode] = useState('')
    const [verifyError, setVerifyError] = useState<string | null>(null)

    useEffect(() => { setFullName(auth?.fullName || '') }, [auth?.fullName])
    useEffect(() => { setUsername(auth?.username || '') }, [auth?.username])
    useEffect(() => {
        setEmail(auth?.email || '')
        setEmailCodeSent(false)
        setEmailCodeError(null)
    }, [auth?.email])

    const debouncedFullNameSave = useMemo(() => debounce((value: string) => dispatch(updateFullNameSaga(value)), DEBOUNCE_INPUT_TIME), [dispatch])
    const debouncedUsernameSave = useMemo(() => debounce((value: string) => dispatch(updateUsernameSaga(value)), DEBOUNCE_INPUT_TIME), [dispatch])

    useEffect(() => {
        return () => {
            debouncedFullNameSave.cancel()
            debouncedUsernameSave.cancel()
        }
    }, [debouncedFullNameSave, debouncedUsernameSave])

    const emailChanged = email !== (auth?.email || '') && !emailError

    const handleFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setFullName(value)
        if (value.trim()) {
            setFullNameError(null)
            debouncedFullNameSave(value.trim())
        } else {
            setFullNameError('Name must be at least 1 character')
        }
    }

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setUsername(value)
        if (value.trim()) {
            setUsernameError(null)
            debouncedUsernameSave(value.trim())
        } else {
            setUsernameError('Username must be at least 1 character')
        }
    }

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setEmail(value)
        setEmailCodeSent(false)
        setEmailCodeError(null)
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            setEmailError('Please enter a valid email address')
        } else {
            setEmailError(null)
        }
    }

    const handleSendEmailVerification = () => {
        setEmailCodeError(null)
        dispatch(sendEmailChangeSaga({
            newEmail: email,
            onSuccess: () => { setEmailCodeSent(true); setVerificationCode(''); setVerifyError(null) },
            onError: (msg) => setEmailCodeError(msg),
        }))
    }

    const handleVerifyEmailChange = () => {
        setVerifyError(null)
        dispatch(verifyEmailChangeSaga({
            code: verificationCode,
            onSuccess: () => { setEmailCodeSent(false); setVerificationCode('') },
            onError: (msg) => setVerifyError(msg),
        }))
    }

    const handleDeleteAccount = () => {
        dispatch(initiateDeleteAccountSaga())
    }

    return (
        <>
            <AccountSettingsTitle>Profile</AccountSettingsTitle>
            <FormDivider />
            <FormColumn>
                <FormColumn gap={16}>
                    <FormTitle>General information</FormTitle>
                    <AccountSettingsAvatar/>
                    <FormColumn>
                        <FormLabel>Full name</FormLabel>
                        <Input value={fullName} onChange={handleFullNameChange} error={!!fullNameError}/>
                        <FormError>{fullNameError}</FormError>
                    </FormColumn>
                    <FormColumn>
                        <FormLabel>Username</FormLabel>
                        <Input value={username} onChange={handleUsernameChange} error={!!usernameError}/>
                        <FormError>{usernameError}</FormError>
                    </FormColumn>
                    <FormColumn>
                        <FormLabel>Email</FormLabel>
                        <Input value={email} onChange={handleEmailChange} error={!!emailError}/>
                        <FormError>{emailError}</FormError>
                        <FormRow align="start">
                            {emailChanged && !emailCodeSent && (
                                <Button
                                    type="black"
                                    title="Send verification link"
                                    onClick={handleSendEmailVerification}
                                />
                            )}
                            {emailChanged && emailCodeSent && (
                                <TextNorm>Code sent to {email}. Enter it below.</TextNorm>
                            )}
                        </FormRow>
                        {emailCodeError && <FormError>{emailCodeError}</FormError>}
                        {emailChanged && emailCodeSent && (
                            <FormRow align="start">
                                <Input
                                    value={verificationCode}
                                    onChange={e => setVerificationCode(e.target.value)}
                                    placeholder="6-digit code"
                                    error={!!verifyError}
                                />
                                <Button
                                    type="black"
                                    title="Confirm"
                                    onClick={handleVerifyEmailChange}
                                />
                            </FormRow>
                        )}
                        {verifyError && <FormError>{verifyError}</FormError>}
                    </FormColumn>
                </FormColumn>
                <FormDivider/>
                <FormColumn gap={16}>
                    <FormTitle>Danger zone</FormTitle>
                    <FormPanel>
                        <Button
                            type="danger_transparent"
                            title="Delete account"
                            icon={<IconTrash/>}
                            onClick={handleDeleteAccount}
                            testId="delete-account-btn"
                        />
                        <TextNorm>This will permanently delete your account. This action cannot be undone.</TextNorm>
                    </FormPanel>
                </FormColumn>
            </FormColumn>
        </>
    )
}

export default AccountSettingsModalProfile
